import { useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { SEO } from '@platform/seo';
import { toUserMessage } from '@platform/api-client';

import { HTTP_STATUS } from '@/shared/constants/app/http.constant.ts';
import { PATHS } from '@/shared/constants/app/routes.constant.ts';
import { buildSeo } from '@/shared/seo/seoBuilder';

import { useRemovePumsemItem } from '../api/use-pumsem-manage.query';
import { usePumsemItem, usePumsemTree } from '../api/use-pumsem.query';
import { usePumsemAdminOverlay } from '../hooks/usePumsemAdminOverlay';
import { PumsemApplicability } from '../components/PumsemApplicability';
import { PumsemChangeBadge } from '../components/PumsemChangeBadge';
import { PumsemBody, PumsemNotes } from '../components/PumsemBody';
import { unplacedNotes } from '../lib/pumsemBody';
import {
  EDITION_PARAM,
  pumsemDiffPath,
  pumsemItemPath,
} from '../lib/pumsemNavigation';
import { PUMSEM_ERROR, codeOf, statusOf } from '../lib/pumsemError';
import { findPumsemNode } from '../lib/pumsemTree';
import { PumsemGroupView } from './PumsemGroupView';
import { PumsemMessage } from './PumsemMessage';

/** 개정 배지에 붙일 짧은 판 표기 — `2026 상반기` → `'26상`. */
function shortEdition(label: string): string {
  const match = /^(\d{2})(\d{2})\s*(상|하)/.exec(label);
  return match ? `'${match[2]}${match[3]}` : label;
}

function ItemSkeleton() {
  return (
    <div className="space-y-3 p-8" aria-hidden>
      <div className="h-5 w-1/2 rounded bg-neutral-100 dark:bg-white/8" />
      <div className="h-4 w-1/3 rounded bg-neutral-100 dark:bg-white/8" />
      <div className="h-48 w-full rounded-xl bg-neutral-100 dark:bg-white/8" />
    </div>
  );
}

/** 품셈 항목 상세. 헤더·결과 목록은 `PumsemLayoutView` 가 그린다. */
export function PumsemItemView() {
  const { division, code } = useParams();
  const [params] = useSearchParams();
  const edition = params.get(EDITION_PARAM);

  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const removeItem = useRemovePumsemItem();

  // 숨긴 판을 보고 있으면 관리 경로에서 받아야 한다 — 공개 경로는 그 판의
  // 존재 자체를 모르므로 404 가 난다.
  //
  // 관리자면 **공개판을 볼 때도** 관리 경로를 쓴다. 편집 주소(`revisionId`)가
  // 그쪽 응답에만 실리기 때문이다. 읽다가 오탈자를 발견하는 게 실제 흐름인데,
  // 진입점이 없어서 관리 목록으로 나갔다 부문·장·절을 다시 펼쳐 같은 항목을
  // 찾아야 했다.
  const { isAdmin, viewingHidden } = usePumsemAdminOverlay(edition);

  // 왼쪽 목차가 이미 받아 둔 트리(같은 캐시 키)를 재사용한다. 여기서 보는 건
  // 하나뿐이다 — 이 코드가 항목인가 장·절인가.
  const { data: tree } = usePumsemTree(edition, viewingHidden);
  const hit = findPumsemNode(tree, division, code);
  const isGroup = hit !== null && !hit.node.isItem;

  const { data: item, isPending, isError, error } = usePumsemItem(
    division,
    code,
    edition,
    isAdmin || viewingHidden,
    !isGroup,
  );

  // 장·절 판정이 오류 분기보다 **먼저**다. 트리가 늦게 도착하면 그전에 쏜
  // 상세 요청이 이미 404 로 끝나 있는데, 그걸 "없는 품셈" 으로 그리면 안 된다.
  if (isGroup && hit) {
    return (
      <PumsemGroupView
        hit={hit}
        edition={edition}
        editionLabel={tree?.edition.label ?? ''}
      />
    );
  }

  if (isError) {
    // 서버 장애를 "없는 품셈"이라고 말하면 사용자는 있지도 않은 오타를 찾는다.
    // 같은 이유로 "판이 하나도 없다" 와 "이 코드가 없다" 도 갈라야 한다 —
    // 첫 설치 직후에는 어떤 코드를 넣어도 404 라서, 품셈명을 찾아보라는
    // 안내가 영원히 헛도는 지시가 된다.
    if (statusOf(error) !== HTTP_STATUS.NOT_FOUND) {
      return (
        <PumsemMessage
          title="품셈을 불러오지 못했습니다."
          body="잠시 후 다시 시도해 주세요."
        />
      );
    }
    if (codeOf(error) === PUMSEM_ERROR.NO_PUBLISHED_EDITION) {
      return (
        <PumsemMessage
          title="아직 공개된 개정판이 없습니다."
          body="관리자가 개정판을 공개하면 품셈을 볼 수 있습니다."
        />
      );
    }
    if (codeOf(error) === PUMSEM_ERROR.EDITION_NOT_FOUND) {
      return (
        <PumsemMessage
          title="이 개정판을 찾을 수 없습니다."
          body="주소의 개정판이 숨겨졌거나 삭제됐습니다. 위 개정판 선택에서 다른 판을 고르세요."
        />
      );
    }
    return (
      <PumsemMessage
        title="해당 품셈을 찾을 수 없습니다."
        body="코드를 다시 확인하시거나, 위 검색으로 품셈명을 찾아보세요."
      />
    );
  }

  if (isPending || !item) return <ItemSkeleton />;

  const editionBadge = shortEdition(item.edition.label);
  const unplaced = unplacedNotes(item.notes, item.body);
  const seo = buildSeo({
    title: `${item.code} ${item.title}`,
    description: `${item.division.title} ${item.code} ${item.title} — ${item.edition.label} 표준품셈${item.unit ? `. 단위 ${item.unit}` : ''}.`,
    path: pumsemItemPath(item.division.slug, item.code, item.edition.slug),
    og: { type: 'article' },
  });

  return (
    <>
      <SEO {...seo} />

      <div className="grid min-w-0 grid-cols-1 gap-6 p-5 sm:p-8 xl:grid-cols-[minmax(0,1fr)_19rem]">
        <article className="min-w-0">
          <nav
            aria-label="위치"
            className="mb-3 text-[13.5px] text-neutral-400"
          >
            <span className="text-neutral-500">{item.division.title}</span>
            {/* 장·절도 주소를 가진다 — 누르면 그 아래 목차가 열린다. 코드 마디를
                지워 가며 범위를 넓히는 동작을 URL 로만 하게 두지 않는다. */}
            {item.breadcrumb.map((node) => (
              <span key={node.code}>
                <span className="px-1.5 text-neutral-300">›</span>
                <Link
                  to={pumsemItemPath(item.division.slug, node.code, edition)}
                  className="text-neutral-500 hover:text-brand-700 hover:underline dark:hover:text-brand-300"
                >
                  {node.code} {node.title}
                </Link>
              </span>
            ))}
          </nav>

          <h1 className="text-[28px] leading-tight font-black tracking-tight text-neutral-900 dark:text-neutral-50">
            <span className="tabular-nums">{item.code}</span>
            <span className="px-2 text-neutral-300">·</span>
            {item.title}
            {/* 발행본은 제목 괄호 안에 개정 연혁을 적는다. 실무자가 눈으로 찾는
                표기라 우리가 계산한 배지와 별개로 원문 그대로 남긴다. */}
            {item.revisionMarks.length > 0 && (
              <span className="ml-2 text-[16px] font-medium text-neutral-400">
                ({item.revisionMarks.join(', ')})
              </span>
            )}
          </h1>

          {/* 관리자만 보이는 편집 진입점. 목록으로 되돌아가지 않고 이 자리에서
              바로 고칠 수 있어야 한다. */}
          {isAdmin && item.revisionId && (
            <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-neutral-300 px-3 py-2 dark:border-white/15">
              <span className="text-[13px] font-bold text-neutral-500">
                관리자
              </span>
              <Link
                to={PATHS.pumsemManageEdit(item.revisionId)}
                className="text-[13.5px] font-bold text-brand-700 hover:underline dark:text-brand-300"
              >
                이 항목 수정
              </Link>
              {confirming ? (
                <>
                  <button
                    type="button"
                    className="text-[13.5px] font-bold text-danger hover:underline"
                    disabled={removeItem.isPending}
                    onClick={() =>
                      removeItem.mutate(item.revisionId!, {
                        onSuccess: () => navigate(PATHS.PUMSEM),
                        onError: (cause) => {
                          setConfirming(false);
                          setActionError(toUserMessage(cause));
                        },
                      })
                    }
                  >
                    {removeItem.isPending ? '빼는 중…' : '정말 삭제'}
                  </button>
                  <button
                    type="button"
                    className="text-[13.5px] text-neutral-400 hover:text-neutral-700"
                    onClick={() => setConfirming(false)}
                  >
                    취소
                  </button>
                  <span className="text-[13px] text-danger">
                    <b>{item.edition.label}</b> 판에서만 빠집니다. 항목 자체와
                    다른 판의 내용, 딥링크는 남습니다.
                  </span>
                </>
              ) : (
                <button
                  type="button"
                  className="text-[13.5px] text-neutral-400 hover:text-danger"
                  onClick={() => {
                    setActionError(null);
                    setConfirming(true);
                  }}
                >
                  이 판에서 삭제
                </button>
              )}
              {actionError && (
                <span className="text-[13px] text-danger" role="alert">
                  {actionError}
                </span>
              )}
            </div>
          )}

          <div className="mt-3 flex flex-wrap items-center gap-1.5">
            {/* 서술 항목(적용기준 계열)은 단위가 없다 — 없는 칸을 그리면
                "단위 당" 같은 빈 배지가 남는다. */}
            {item.unit && (
              <span className="rounded-md border border-neutral-200 px-2 py-1 text-[13px] text-neutral-600 dark:border-white/12 dark:text-neutral-300">
                단위 {item.unit}당
              </span>
            )}
            <span className="rounded-md border border-brand-500 bg-brand-50 px-2 py-1 text-[13px] font-bold text-brand-700 dark:border-brand-500/40 dark:bg-brand-500/10 dark:text-brand-300">
              {item.edition.label}
            </span>
            <PumsemChangeBadge
              change={item.changeKind}
              editionLabel={editionBadge}
            />

            <div className="ml-auto flex items-center gap-2">
              {/* 판 고정 링크. 지금 보고 있는 URL 이 "현행"이면 판이 안 붙어 있어
                  반기마다 내용이 바뀐다 — 견적서 근거로는 이 링크를 써야 한다. */}
              <Link
                to={pumsemItemPath(
                  item.division.slug,
                  item.code,
                  item.edition.slug,
                )}
                replace
                className="rounded-lg border border-neutral-200 px-2.5 py-1.5 text-[13.5px] text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-white/12 dark:text-neutral-200 dark:hover:bg-white/5"
              >
                이 판 고정 링크
              </Link>
              {/*
                비교는 **공개 경로에만** 있다(`manage/diff` 가 없다). 숨긴 판을
                오버레이로 보는 관리자에게 이 버튼을 그대로 두면, 눌렀을 때
                "비교할 직전 개정판이 없습니다" 가 뜬다 — 관리자는 "직전 판에
                이 항목이 없구나" 라고 잘못 결론짓는다. 실제로는 미공개 판이라
                비교 API 를 못 쓰는 것뿐이다.
              */}
              {item.hasPreviousEdition &&
                (viewingHidden ? (
                  <span className="rounded-lg border border-dashed border-neutral-200 px-2.5 py-1.5 text-[13.5px] text-neutral-400 dark:border-white/12">
                    숨긴 판은 비교를 제공하지 않습니다
                  </span>
                ) : (
                  <Link
                    to={pumsemDiffPath(
                      item.division.slug,
                      item.code,
                      item.edition.slug,
                    )}
                    className="rounded-lg border border-neutral-200 px-2.5 py-1.5 text-[13.5px] text-neutral-700 transition-colors hover:bg-neutral-50 dark:border-white/12 dark:text-neutral-200 dark:hover:bg-white/5"
                  >
                    직전 판 비교
                  </Link>
                ))}
            </div>
          </div>

          {item.changeSummary && (
            <p className="mt-4 rounded-xl border border-warning/40 bg-warning/10 px-4 py-3 text-[14.5px] leading-6 text-amber-900 dark:text-amber-200">
              <b>이번 판 변경</b> — {item.changeSummary}
            </p>
          )}

          {/* 문단·표·그림·산식이 발행본 순서 그대로 한 배열에서 나온다. */}
          <PumsemBody body={item.body} editionLabel={editionBadge} />

          {/* 본문에 자리를 잡은 [주]는 위에서 그렸다. 남은 것만 맨 아래에 둔다 —
              파생(적용 조건)이 틀려도 원문은 화면에 그대로 남아야 한다. */}
          {unplaced.length > 0 && (
            <div className="mt-6">
              <PumsemNotes notes={unplaced} />
            </div>
          )}

          <nav className="mt-8 grid grid-cols-2 gap-3">
            {item.prevCode ? (
              <Link
                to={pumsemItemPath(item.division.slug, item.prevCode, edition)}
                className="rounded-xl border border-neutral-200 px-4 py-3 transition-colors hover:bg-neutral-50 dark:border-white/12 dark:hover:bg-white/5"
              >
                <span className="block text-[13px] text-neutral-400">이전</span>
                <span className="text-[15px] font-bold tabular-nums">
                  {item.prevCode}
                </span>
              </Link>
            ) : (
              <span />
            )}
            {item.nextCode && (
              <Link
                to={pumsemItemPath(item.division.slug, item.nextCode, edition)}
                className="rounded-xl border border-neutral-200 px-4 py-3 text-right transition-colors hover:bg-neutral-50 dark:border-white/12 dark:hover:bg-white/5"
              >
                <span className="block text-[13px] text-neutral-400">다음</span>
                <span className="text-[15px] font-bold tabular-nums">
                  {item.nextCode}
                </span>
              </Link>
            )}
          </nav>
        </article>

        <aside className="space-y-5 xl:sticky xl:top-20 xl:self-start">
          <PumsemApplicability applicability={item.applicability} />

          {item.history.length > 0 && (
            <section>
              <h2 className="mb-2 text-[12.5px] font-bold tracking-wide text-neutral-400">
                개정 이력
              </h2>
              <ul className="space-y-2">
                {item.history.map((entry) => (
                  <li key={entry.edition} className="flex gap-2.5">
                    <span className="w-[4.5rem] shrink-0 text-[13px] font-bold tabular-nums text-neutral-500">
                      {entry.label}
                    </span>
                    <span className="text-[13.5px] leading-5 text-neutral-600 dark:text-neutral-400">
                      {entry.summary ?? changeText(entry.changeKind)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </aside>
      </div>
    </>
  );
}

/** 요약을 안 적은 판의 대체 문구. 요약은 사람이 쓰므로 빌 수 있다. */
function changeText(change: string): string {
  if (change === 'NEW') return '신설';
  if (change === 'AMENDED') return '개정';
  if (change === 'REMOVED') return '삭제';
  return '변경 없음';
}
